// longSocket.js
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

// Хачапури использует отдельный namespace /khachapuri.
// Пока пользователь находится на странице Хачапури, старый longSocket не должен
// восстанавливать сохранённый long-стол и тем более редиректить на /longGame.
function isKhachapuriContext() {
  try {
    if (typeof window === 'undefined') return false;

    const path = String(window.location?.pathname || '');
    if (path.startsWith('/khachapuriGame/')) return true;

    return localStorage.getItem('gameType') === 'khachapuri';
  } catch (_) {
    return false;
  }
}

/* ──────────────────────────────
   Утилита для человекочитаемых ошибок
────────────────────────────── */
function toErrText(x) {
  if (!x) return 'Неизвестная ошибка';
  if (typeof x === 'string') return x;
  const any = x;
  if (any.message) return String(any.message);
  if (any.error) return typeof any.error === 'string' ? any.error : JSON.stringify(any.error);
  if (any.reason) return String(any.reason);
  try { return JSON.stringify(any); } catch { return String(any); }
}






// Ищем снап вида "long:snap:XXX" в localStorage и достаём tableId + имя игрока
function getSnapRoomFromLocalStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;

    const storage = window.localStorage;
    const prefix  = 'long:snap:';
    const keys    = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) {
        keys.push(key);
      }
    }

    if (!keys.length) return null;

    // обычно у игрока один снап, возьмём последний
    const snapKey = keys[keys.length - 1];
    const raw     = storage.getItem(snapKey);
    if (!raw) return null;

    const snap = JSON.parse(raw);

    const rawTableId =
      snap.tableId ??
      snap.table_id ??
      snap.long_table_id;

    const tableId = Number(rawTableId);
    if (!Number.isFinite(tableId)) return null;

    const username =
      storage.getItem('username') ||
      snap.p1 ||
      snap.p2 ||
      null;

    if (!username) return null;

    return { tableId, playerName: username };
  } catch (e) {
    console.warn('[longSocket] getSnapRoomFromLocalStorage error:', e);
    return null;
  }
}


/* ──────────────────────────────
   Создаём клиент Socket.IO
────────────────────────────── */
const longSocket = io(`${SOCKET_URL}/long`, {
  autoConnect: false,
  transports: ['websocket'],         // при отладке можно временно убрать
  withCredentials: false,

  // 🔁 Параметры реконнекта
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 500,
  reconnectionDelayMax: 5000,

  // ⏱️ Таймаут установления соединения
  timeout: 20000,
});

/* ──────────────────────────────
   Память о текущем "столе" для авто-реjoin
────────────────────────────── */
// здесь запоминаем последний стол, чтобы после реконнекта снова в него зайти
// структура: { tableId, playerName }
let currentLongRoom = null;

/**
 * Подтянуть токен из localStorage и положить в longSocket.auth
 */
function refreshAuthFromLocalStorage() {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      longSocket.auth = { token };
    }
  } catch (e) {
    console.warn('[longSocket] error reading token from localStorage:', e);
  }
}

/**
 * Достать из памяти / localStorage данные о последнем столе
 */
function loadLongRoomFromCache() {
  try {
    const raw = localStorage.getItem('long:lastRoom') || 'null';
    const saved = JSON.parse(raw);
    if (saved && saved.tableId && saved.playerName) {
      currentLongRoom = saved;
      return currentLongRoom;
    }
  } catch (e) {
    console.warn('[longSocket] error reading lastRoom from localStorage:', e);
  }
  return currentLongRoom;
}


/**
 * Автоматически пере-войти за стол после (ре)коннекта
 * ВНИМАНИЕ: БЕЗ callback/ack, просто emit — сервер у тебя не вызывает ack.
 */
function rejoinIfNeeded() {
  try {
    // Не вмешиваемся в отдельную cash-игру Хачапури.
    if (isKhachapuriContext()) return;
    // 1) Берём то, что когда-то сохранили через rememberLongRoom (если есть)
    let info = loadLongRoomFromCache() || currentLongRoom || null;

    // 2) Пробуем уточнить id стола по снапу из localStorage (long:snap:XXX)
    const snapRoom = getSnapRoomFromLocalStorage();
    if (snapRoom) {
      // если в lastRoom уже есть playerName, оставим его, а tableId возьмём из снапа
      if (info) {
        info = {
          tableId: snapRoom.tableId,
          playerName: info.playerName || snapRoom.playerName,
        };
      } else {
        // если lastRoom не было вовсе — берём всё из снапа
        info = snapRoom;
      }
    }

    // Если в итоге так и не смогли собрать info — просто выходим
    if (!info || !info.tableId || !info.playerName) {
      return;
    }

    currentLongRoom = info;

    console.log(
      '[rejoin] trying to rejoin long table',
      info.tableId,
      'as',
      info.playerName
    );

    longSocket.emit('joinLongTable', {
      longTableId: Number(info.tableId),
      playerUsername: info.playerName,
      isSpectator: false,
    });
  } catch (e) {
    console.warn('[longSocket] rejoinIfNeeded error:', e);
  }
}



/* ──────────────────────────────
   Экспортируемые утилиты
────────────────────────────── */

/**
 * Гарантированно подтянуть токен и подключиться.
 * Вызывай при маунте клиента и по кнопке "Подключить сейчас".
 */
export function ensureLongSocketAuthAndConnect() {
  refreshAuthFromLocalStorage();
  if (!longSocket.connected) {
    longSocket.connect();
  }
}

/**
 * Запомнить стол для авто-реjoin после реконнекта.
 * ВАЖНО: теперь можно вызывать СРАЗУ после emit joinLongTable (без ack).
 */
export function rememberLongRoom(tableId, playerName) {
  currentLongRoom = { tableId, playerName };
  try {
    localStorage.setItem('long:lastRoom', JSON.stringify(currentLongRoom));
  } catch (e) {
    console.warn('[longSocket] error saving lastRoom to localStorage:', e);
  }
}

/**
 * Очистить сохранённый стол.
 * Вызывай ПОСЛЕ успешного выхода (longLeaveTable) и по кнопке "Покинуть стол".
 */
export function clearLongRoom() {
  currentLongRoom = null;
  try {
    localStorage.removeItem('long:lastRoom');

    // заодно чистим все снапы длинных партий
    if (typeof window !== 'undefined' && window.localStorage) {
      const storage = window.localStorage;
      const prefix  = 'long:snap:';
      const toDelete = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) {
          toDelete.push(key);
        }
      }

      toDelete.forEach((k) => storage.removeItem(k));
    }
  } catch (e) {
    console.warn('[longSocket] error clearing room from localStorage:', e);
  }
}


/* ──────────────────────────────
   Глобальные обработчики соединения
   (через window.dispatchEvent → удобно подписаться из компонентов)
────────────────────────────── */

// успешное соединение
longSocket.on('connect', () => {
  console.log('[longSocket] connected, id =', longSocket.id);

  // На всякий случай подтянем свежий токен (если успел смениться)
  refreshAuthFromLocalStorage();

  // Пере-войти за стол, если есть сохранённый
  rejoinIfNeeded();

  window.dispatchEvent(new CustomEvent('long-conn', {
    detail: { state: 'online' },
  }));
});

// потеря соединения
longSocket.on('disconnect', (reason) => {
  console.warn('[longSocket] disconnected:', reason);

  window.dispatchEvent(new CustomEvent('long-conn', {
    detail: { state: 'offline', reason: toErrText(reason) },
  }));
});

// ошибки при коннекте
longSocket.on('connect_error', (err) => {
  console.error('[longSocket] connect_error:', err);


    // ✅ ДОБАВЬ ВОТ ЭТО:
  const msg = String(err?.message || '');
  if (msg.includes('SESSION_EXPIRED')) {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    window.location.href = '/login';
    return;
  }
  
  window.dispatchEvent(new CustomEvent('long-conn', {
    detail: { state: 'error', message: toErrText(err) },
  }));
});

// попытка реконнекта (через менеджер сокета)
longSocket.io.on('reconnect_attempt', (attempt) => {
  console.log('[longSocket] reconnect_attempt:', attempt);

  // перед каждой попыткой — освежаем токен
  refreshAuthFromLocalStorage();

  window.dispatchEvent(new CustomEvent('long-conn', {
    detail: { state: 'reconnecting', attempt },
  }));
});

// успешно реконнектнулись
longSocket.io.on('reconnect', (attempt) => {
  console.log('[longSocket] reconnect success, attempt =', attempt);

  window.dispatchEvent(new CustomEvent('long-conn', {
    detail: { state: 'online', recovered: true, attempt },
  }));
  // далее ещё раз сработает обычный 'connect', где вызовется rejoinIfNeeded()
});





/*
 * Сервер запретил открывать второй cash-стол
 * и возвращает пользователя в уже активную игру.
 */
longSocket.on(
  'long_forceActiveCashTable',
  (payload) => {
    // Событие старого long cash не имеет права увести страницу Хачапури.
    if (isKhachapuriContext()) return;
    const activeTableId =
      Number(payload?.activeTableId);

    const activeType =
      payload?.activeType === 'short'
        ? 'short'
        : 'long';

    if (
      !Number.isInteger(activeTableId) ||
      activeTableId <= 0
    ) {
      console.warn(
        '[LONG CASH] неверный activeTableId:',
        payload
      );

      return;
    }

    console.warn(
      '[LONG CASH] возврат в активный стол:',
      payload
    );

    /*
     * Пользователь уже играет в короткие.
     */
    if (activeType === 'short') {
      clearLongRoom();

      try {
        localStorage.setItem(
          'currentTableId',
          String(activeTableId)
        );

        localStorage.setItem(
          'gameType',
          'short'
        );
      } catch (e) {}

      const targetPath =
        `/ShortBackgammonBoard/${activeTableId}`;

      if (window.location.pathname !== targetPath) {
        window.location.replace(targetPath);
      }

      return;
    }

    /*
     * Пользователь уже играет за другим длинным столом.
     */
    const playerName =
      localStorage.getItem('username') || '';

    clearLongRoom();

    if (playerName) {
      rememberLongRoom(
        activeTableId,
        playerName
      );
    }

    try {
      localStorage.removeItem('currentTableId');
      localStorage.setItem('gameType', 'long');
    } catch (e) {}

    const targetPath =
      `/longGame/${activeTableId}`;

    if (window.location.pathname !== targetPath) {
      window.location.replace(targetPath);
    }
  }
);


/*
 * Старое серверное событие редиректа длинного стола.
 * Раньше компонент длинной доски его не слушал.
 */
longSocket.on('long_redirectToTable', (payload) => {
  // Не обрабатываем long-редиректы внутри отдельной игры Хачапури.
  if (isKhachapuriContext()) return;
  const tableId =
    Number(payload?.to ?? payload?.tableId);

  if (
    !Number.isInteger(tableId) ||
    tableId <= 0
  ) {
    return;
  }

  const playerName =
    localStorage.getItem('username') || '';

  clearLongRoom();

  if (playerName) {
    rememberLongRoom(tableId, playerName);
  }

  const targetPath =
    `/longGame/${tableId}`;

  if (window.location.pathname !== targetPath) {
    window.location.replace(targetPath);
  }
});



export default longSocket;




// // longSocket.js
// import { io } from 'socket.io-client';

// const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';








// function toErrText(x) {
//   if (!x) return 'Неизвестная ошибка';
//   if (typeof x === 'string') return x;
//   const any = x;
//   if (any.message) return String(any.message);
//   if (any.error) return typeof any.error === 'string' ? any.error : JSON.stringify(any.error);
//   if (any.reason) return String(any.reason);
//   try { return JSON.stringify(any); } catch { return String(any); }
// }

// /**
//  * Создаём клиент Socket.IO с включённым автопереподключением.
//  */
// const longSocket = io(`${SOCKET_URL}/long`, {
//   autoConnect: false,
//   transports: ['websocket'],         // при отладке можно временно убрать
//   withCredentials: false,

//   // 🔁 Параметры реконнекта
//   reconnection: true,
//   reconnectionAttempts: Infinity,
//   reconnectionDelay: 500,
//   reconnectionDelayMax: 5000,

//   // ⏱️ Таймаут установления соединения
//   timeout: 20000,
// });

// /* ──────────────────────────────
//    Память о текущем "столе" для авто-реjoin
// ────────────────────────────── */
// let currentLongRoom = null;   // { tableId, playerName }
// let rejoinInFlight = false;   // защита от двойных join во время реконнекта

// function refreshAuthFromLocalStorage() {
//   try {
//     const token = localStorage.getItem('token');
//     if (token) longSocket.auth = { token };
//   } catch {}
// }

// function loadLongRoomFromCache() {
//   if (currentLongRoom) return currentLongRoom;
//   try {
//     const saved = JSON.parse(localStorage.getItem('long:lastRoom') || 'null');
//     if (saved && saved.tableId) currentLongRoom = saved;
//   } catch {}
//   return currentLongRoom;
// }

// function rejoinIfNeeded() {
//   const info = loadLongRoomFromCache();
//   if (!info || rejoinInFlight) return;

//   rejoinInFlight = true;
// longSocket.emit(
//   'joinLongTable',
//   {
//     longTableId: Number(info.tableId),     // 👈 нужное имя поля
//     playerUsername: info.playerName,       // 👈 нужное имя поля
//     isSpectator: false
//   },
//   (ack = {}) => {
//     rejoinInFlight = false;
//     if (ack.ok) {
//       console.log('[rejoin] back to long table', info.tableId);
//     } else {
//       const msg = toErrText(ack);
//       console.warn('[rejoin] joinLongTable error:', msg, ack);
//       // при желании: clearLongRoom();
//     }
//   }
// );


// }

// /* ──────────────────────────────
//    Экспортируемые утилиты
// ────────────────────────────── */

// /**
//  * Гарантированно подтянуть токен и подключиться.
//  * Вызывай при маунте клиента и по кнопке "Подключить сейчас".
//  */
// export function ensureLongSocketAuthAndConnect() {
//   refreshAuthFromLocalStorage();
//   if (!longSocket.connected) {
//     longSocket.connect();
//   }
// }

// /**
//  * Запомнить стол для авто-реjoin после реконнекта.
//  * Вызывай ПОСЛЕ успешного ack joinLongTable на клиенте.
//  */
// export function rememberLongRoom(tableId, playerName) {
//   currentLongRoom = { tableId, playerName };
//   try { localStorage.setItem('long:lastRoom', JSON.stringify(currentLongRoom)); } catch {}
// }

// /**
//  * Очистить сохранённый стол.
//  * Вызывай ПОСЛЕ успешного ack longLeaveTable на клиенте (и по кнопке "Покинуть стол").
//  */
// export function clearLongRoom() {
//   currentLongRoom = null;
//   try { localStorage.removeItem('long:lastRoom'); } catch {}
// }

// /* ──────────────────────────────
//    Глобальные обработчики соединения
//    (через window.dispatchEvent → удобно подписаться из компонентов)
// ────────────────────────────── */

// // успешное соединение
// longSocket.on('connect', () => {
//   // На всякий случай подтянем свежий токен (если успел смениться)
//   refreshAuthFromLocalStorage();

//   // Пере-войти за стол, если есть сохранённый
//   rejoinIfNeeded();

//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'online' },
//   }));
// });

// // потеря соединения
// longSocket.on('disconnect', (reason) => {
//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'offline', reason: toErrText(reason) },
//   }));
// });

// // ошибки при коннекте
// longSocket.on('connect_error', (err) => {
//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'error', message: toErrText(err) },
//   }));
// });

// // попытка реконнекта (через менеджер сокета)
// longSocket.io.on('reconnect_attempt', (attempt) => {
//   // перед каждой попыткой — освежаем токен
//   refreshAuthFromLocalStorage();

//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'reconnecting', attempt },
//   }));
// });

// // успешно реконнектнулись
// longSocket.io.on('reconnect', (attempt) => {
//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'online', recovered: true, attempt },
//   }));
//   // далее сработает обычный 'connect', где вызовется rejoinIfNeeded()
// });

// export default longSocket;


// import { io } from 'socket.io-client';

// const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

// const longSocket = io(`${SOCKET_URL}/long`, {
//   autoConnect: false,
//   transports: ['websocket'], // при отладке можно временно убрать
//   withCredentials: false,
// });

// /* ⬇️ ДОБАВЬ */
// export function ensureLongSocketAuthAndConnect() {
//   try {
//     const token = localStorage.getItem('token');
//     if (token) longSocket.auth = { token };
//   } catch {}
//   if (!longSocket.connected && !longSocket.connecting) longSocket.connect();
// }
// /* ⬆️ ДОБАВЛЕНО */

// export default longSocket;






// // longSocket.js
// import { io } from 'socket.io-client';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// const longSocket = io(`${API_URL}/long`, {
//   autoConnect: false,
//   transports: ['websocket'],   // ← ОБЯЗАТЕЛЬНО
//   withCredentials: false,
// });

// export default longSocket;


// // longSocket.js
// import { io } from 'socket.io-client';

// const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

// /* ──────────────────────────────
//    Утилита для человекочитаемых ошибок
// ────────────────────────────── */
// function toErrText(x) {
//   if (!x) return 'Неизвестная ошибка';
//   if (typeof x === 'string') return x;
//   const any = x;
//   if (any.message) return String(any.message);
//   if (any.error) return typeof any.error === 'string' ? any.error : JSON.stringify(any.error);
//   if (any.reason) return String(any.reason);
//   try { return JSON.stringify(any); } catch { return String(any); }
// }






// // Ищем снап вида "long:snap:XXX" в localStorage и достаём tableId + имя игрока
// function getSnapRoomFromLocalStorage() {
//   try {
//     if (typeof window === 'undefined' || !window.localStorage) return null;

//     const storage = window.localStorage;
//     const prefix  = 'long:snap:';
//     const keys    = [];

//     for (let i = 0; i < storage.length; i++) {
//       const key = storage.key(i);
//       if (key && key.startsWith(prefix)) {
//         keys.push(key);
//       }
//     }

//     if (!keys.length) return null;

//     // обычно у игрока один снап, возьмём последний
//     const snapKey = keys[keys.length - 1];
//     const raw     = storage.getItem(snapKey);
//     if (!raw) return null;

//     const snap = JSON.parse(raw);

//     const rawTableId =
//       snap.tableId ??
//       snap.table_id ??
//       snap.long_table_id;

//     const tableId = Number(rawTableId);
//     if (!Number.isFinite(tableId)) return null;

//     const username =
//       storage.getItem('username') ||
//       snap.p1 ||
//       snap.p2 ||
//       null;

//     if (!username) return null;

//     return { tableId, playerName: username };
//   } catch (e) {
//     console.warn('[longSocket] getSnapRoomFromLocalStorage error:', e);
//     return null;
//   }
// }


// /* ──────────────────────────────
//    Создаём клиент Socket.IO
// ────────────────────────────── */
// const longSocket = io(`${SOCKET_URL}/long`, {
//   autoConnect: false,
//   transports: ['websocket'],         // при отладке можно временно убрать
//   withCredentials: false,

//   // 🔁 Параметры реконнекта
//   reconnection: true,
//   reconnectionAttempts: Infinity,
//   reconnectionDelay: 500,
//   reconnectionDelayMax: 5000,

//   // ⏱️ Таймаут установления соединения
//   timeout: 20000,
// });

// /* ──────────────────────────────
//    Память о текущем "столе" для авто-реjoin
// ────────────────────────────── */
// // здесь запоминаем последний стол, чтобы после реконнекта снова в него зайти
// // структура: { tableId, playerName }
// let currentLongRoom = null;

// /**
//  * Подтянуть токен из localStorage и положить в longSocket.auth
//  */
// function refreshAuthFromLocalStorage() {
//   try {
//     const token = localStorage.getItem('token');
//     if (token) {
//       longSocket.auth = { token };
//     }
//   } catch (e) {
//     console.warn('[longSocket] error reading token from localStorage:', e);
//   }
// }

// /**
//  * Достать из памяти / localStorage данные о последнем столе
//  */
// function loadLongRoomFromCache() {
//   try {
//     const raw = localStorage.getItem('long:lastRoom') || 'null';
//     const saved = JSON.parse(raw);
//     if (saved && saved.tableId && saved.playerName) {
//       currentLongRoom = saved;
//       return currentLongRoom;
//     }
//   } catch (e) {
//     console.warn('[longSocket] error reading lastRoom from localStorage:', e);
//   }
//   return currentLongRoom;
// }


// /**
//  * Автоматически пере-войти за стол после (ре)коннекта
//  * ВНИМАНИЕ: БЕЗ callback/ack, просто emit — сервер у тебя не вызывает ack.
//  */
// function rejoinIfNeeded() {
//   try {
//     // 1) Берём то, что когда-то сохранили через rememberLongRoom (если есть)
//     let info = loadLongRoomFromCache() || currentLongRoom || null;

//     // 2) Пробуем уточнить id стола по снапу из localStorage (long:snap:XXX)
//     const snapRoom = getSnapRoomFromLocalStorage();
//     if (snapRoom) {
//       // если в lastRoom уже есть playerName, оставим его, а tableId возьмём из снапа
//       if (info) {
//         info = {
//           tableId: snapRoom.tableId,
//           playerName: info.playerName || snapRoom.playerName,
//         };
//       } else {
//         // если lastRoom не было вовсе — берём всё из снапа
//         info = snapRoom;
//       }
//     }

//     // Если в итоге так и не смогли собрать info — просто выходим
//     if (!info || !info.tableId || !info.playerName) {
//       return;
//     }

//     currentLongRoom = info;

//     console.log(
//       '[rejoin] trying to rejoin long table',
//       info.tableId,
//       'as',
//       info.playerName
//     );

//     longSocket.emit('joinLongTable', {
//       longTableId: Number(info.tableId),
//       playerUsername: info.playerName,
//       isSpectator: false,
//     });
//   } catch (e) {
//     console.warn('[longSocket] rejoinIfNeeded error:', e);
//   }
// }



// /* ──────────────────────────────
//    Экспортируемые утилиты
// ────────────────────────────── */

// /**
//  * Гарантированно подтянуть токен и подключиться.
//  * Вызывай при маунте клиента и по кнопке "Подключить сейчас".
//  */
// export function ensureLongSocketAuthAndConnect() {
//   refreshAuthFromLocalStorage();
//   if (!longSocket.connected) {
//     longSocket.connect();
//   }
// }

// /**
//  * Запомнить стол для авто-реjoin после реконнекта.
//  * ВАЖНО: теперь можно вызывать СРАЗУ после emit joinLongTable (без ack).
//  */
// export function rememberLongRoom(tableId, playerName) {
//   currentLongRoom = { tableId, playerName };
//   try {
//     localStorage.setItem('long:lastRoom', JSON.stringify(currentLongRoom));
//   } catch (e) {
//     console.warn('[longSocket] error saving lastRoom to localStorage:', e);
//   }
// }

// /**
//  * Очистить сохранённый стол.
//  * Вызывай ПОСЛЕ успешного выхода (longLeaveTable) и по кнопке "Покинуть стол".
//  */
// export function clearLongRoom() {
//   currentLongRoom = null;
//   try {
//     localStorage.removeItem('long:lastRoom');

//     // заодно чистим все снапы длинных партий
//     if (typeof window !== 'undefined' && window.localStorage) {
//       const storage = window.localStorage;
//       const prefix  = 'long:snap:';
//       const toDelete = [];

//       for (let i = 0; i < storage.length; i++) {
//         const key = storage.key(i);
//         if (key && key.startsWith(prefix)) {
//           toDelete.push(key);
//         }
//       }

//       toDelete.forEach((k) => storage.removeItem(k));
//     }
//   } catch (e) {
//     console.warn('[longSocket] error clearing room from localStorage:', e);
//   }
// }


// /* ──────────────────────────────
//    Глобальные обработчики соединения
//    (через window.dispatchEvent → удобно подписаться из компонентов)
// ────────────────────────────── */

// // успешное соединение
// longSocket.on('connect', () => {
//   console.log('[longSocket] connected, id =', longSocket.id);

//   // На всякий случай подтянем свежий токен (если успел смениться)
//   refreshAuthFromLocalStorage();

//   // Пере-войти за стол, если есть сохранённый
//   rejoinIfNeeded();

//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'online' },
//   }));
// });

// // потеря соединения
// longSocket.on('disconnect', (reason) => {
//   console.warn('[longSocket] disconnected:', reason);

//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'offline', reason: toErrText(reason) },
//   }));
// });

// // ошибки при коннекте
// longSocket.on('connect_error', (err) => {
//   console.error('[longSocket] connect_error:', err);


//     // ✅ ДОБАВЬ ВОТ ЭТО:
//   const msg = String(err?.message || '');
//   if (msg.includes('SESSION_EXPIRED')) {
//     localStorage.removeItem('token');
//     localStorage.removeItem('username');
//     window.location.href = '/login';
//     return;
//   }
  
//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'error', message: toErrText(err) },
//   }));
// });

// // попытка реконнекта (через менеджер сокета)
// longSocket.io.on('reconnect_attempt', (attempt) => {
//   console.log('[longSocket] reconnect_attempt:', attempt);

//   // перед каждой попыткой — освежаем токен
//   refreshAuthFromLocalStorage();

//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'reconnecting', attempt },
//   }));
// });

// // успешно реконнектнулись
// longSocket.io.on('reconnect', (attempt) => {
//   console.log('[longSocket] reconnect success, attempt =', attempt);

//   window.dispatchEvent(new CustomEvent('long-conn', {
//     detail: { state: 'online', recovered: true, attempt },
//   }));
//   // далее ещё раз сработает обычный 'connect', где вызовется rejoinIfNeeded()
// });





// /*
//  * Сервер запретил открывать второй cash-стол
//  * и возвращает пользователя в уже активную игру.
//  */
// longSocket.on(
//   'long_forceActiveCashTable',
//   (payload) => {
//     const activeTableId =
//       Number(payload?.activeTableId);

//     const activeType =
//       payload?.activeType === 'short'
//         ? 'short'
//         : 'long';

//     if (
//       !Number.isInteger(activeTableId) ||
//       activeTableId <= 0
//     ) {
//       console.warn(
//         '[LONG CASH] неверный activeTableId:',
//         payload
//       );

//       return;
//     }

//     console.warn(
//       '[LONG CASH] возврат в активный стол:',
//       payload
//     );

//     /*
//      * Пользователь уже играет в короткие.
//      */
//     if (activeType === 'short') {
//       clearLongRoom();

//       try {
//         localStorage.setItem(
//           'currentTableId',
//           String(activeTableId)
//         );

//         localStorage.setItem(
//           'gameType',
//           'short'
//         );
//       } catch (e) {}

//       const targetPath =
//         `/ShortBackgammonBoard/${activeTableId}`;

//       if (window.location.pathname !== targetPath) {
//         window.location.replace(targetPath);
//       }

//       return;
//     }

//     /*
//      * Пользователь уже играет за другим длинным столом.
//      */
//     const playerName =
//       localStorage.getItem('username') || '';

//     clearLongRoom();

//     if (playerName) {
//       rememberLongRoom(
//         activeTableId,
//         playerName
//       );
//     }

//     try {
//       localStorage.removeItem('currentTableId');
//       localStorage.setItem('gameType', 'long');
//     } catch (e) {}

//     const targetPath =
//       `/longGame/${activeTableId}`;

//     if (window.location.pathname !== targetPath) {
//       window.location.replace(targetPath);
//     }
//   }
// );


// /*
//  * Старое серверное событие редиректа длинного стола.
//  * Раньше компонент длинной доски его не слушал.
//  */
// longSocket.on('long_redirectToTable', (payload) => {
//   const tableId =
//     Number(payload?.to ?? payload?.tableId);

//   if (
//     !Number.isInteger(tableId) ||
//     tableId <= 0
//   ) {
//     return;
//   }

//   const playerName =
//     localStorage.getItem('username') || '';

//   clearLongRoom();

//   if (playerName) {
//     rememberLongRoom(tableId, playerName);
//   }

//   const targetPath =
//     `/longGame/${tableId}`;

//   if (window.location.pathname !== targetPath) {
//     window.location.replace(targetPath);
//   }
// });



// export default longSocket;




// // // longSocket.js
// // import { io } from 'socket.io-client';

// // const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';








// // function toErrText(x) {
// //   if (!x) return 'Неизвестная ошибка';
// //   if (typeof x === 'string') return x;
// //   const any = x;
// //   if (any.message) return String(any.message);
// //   if (any.error) return typeof any.error === 'string' ? any.error : JSON.stringify(any.error);
// //   if (any.reason) return String(any.reason);
// //   try { return JSON.stringify(any); } catch { return String(any); }
// // }

// // /**
// //  * Создаём клиент Socket.IO с включённым автопереподключением.
// //  */
// // const longSocket = io(`${SOCKET_URL}/long`, {
// //   autoConnect: false,
// //   transports: ['websocket'],         // при отладке можно временно убрать
// //   withCredentials: false,

// //   // 🔁 Параметры реконнекта
// //   reconnection: true,
// //   reconnectionAttempts: Infinity,
// //   reconnectionDelay: 500,
// //   reconnectionDelayMax: 5000,

// //   // ⏱️ Таймаут установления соединения
// //   timeout: 20000,
// // });

// // /* ──────────────────────────────
// //    Память о текущем "столе" для авто-реjoin
// // ────────────────────────────── */
// // let currentLongRoom = null;   // { tableId, playerName }
// // let rejoinInFlight = false;   // защита от двойных join во время реконнекта

// // function refreshAuthFromLocalStorage() {
// //   try {
// //     const token = localStorage.getItem('token');
// //     if (token) longSocket.auth = { token };
// //   } catch {}
// // }

// // function loadLongRoomFromCache() {
// //   if (currentLongRoom) return currentLongRoom;
// //   try {
// //     const saved = JSON.parse(localStorage.getItem('long:lastRoom') || 'null');
// //     if (saved && saved.tableId) currentLongRoom = saved;
// //   } catch {}
// //   return currentLongRoom;
// // }

// // function rejoinIfNeeded() {
// //   const info = loadLongRoomFromCache();
// //   if (!info || rejoinInFlight) return;

// //   rejoinInFlight = true;
// // longSocket.emit(
// //   'joinLongTable',
// //   {
// //     longTableId: Number(info.tableId),     // 👈 нужное имя поля
// //     playerUsername: info.playerName,       // 👈 нужное имя поля
// //     isSpectator: false
// //   },
// //   (ack = {}) => {
// //     rejoinInFlight = false;
// //     if (ack.ok) {
// //       console.log('[rejoin] back to long table', info.tableId);
// //     } else {
// //       const msg = toErrText(ack);
// //       console.warn('[rejoin] joinLongTable error:', msg, ack);
// //       // при желании: clearLongRoom();
// //     }
// //   }
// // );


// // }

// // /* ──────────────────────────────
// //    Экспортируемые утилиты
// // ────────────────────────────── */

// // /**
// //  * Гарантированно подтянуть токен и подключиться.
// //  * Вызывай при маунте клиента и по кнопке "Подключить сейчас".
// //  */
// // export function ensureLongSocketAuthAndConnect() {
// //   refreshAuthFromLocalStorage();
// //   if (!longSocket.connected) {
// //     longSocket.connect();
// //   }
// // }

// // /**
// //  * Запомнить стол для авто-реjoin после реконнекта.
// //  * Вызывай ПОСЛЕ успешного ack joinLongTable на клиенте.
// //  */
// // export function rememberLongRoom(tableId, playerName) {
// //   currentLongRoom = { tableId, playerName };
// //   try { localStorage.setItem('long:lastRoom', JSON.stringify(currentLongRoom)); } catch {}
// // }

// // /**
// //  * Очистить сохранённый стол.
// //  * Вызывай ПОСЛЕ успешного ack longLeaveTable на клиенте (и по кнопке "Покинуть стол").
// //  */
// // export function clearLongRoom() {
// //   currentLongRoom = null;
// //   try { localStorage.removeItem('long:lastRoom'); } catch {}
// // }

// // /* ──────────────────────────────
// //    Глобальные обработчики соединения
// //    (через window.dispatchEvent → удобно подписаться из компонентов)
// // ────────────────────────────── */

// // // успешное соединение
// // longSocket.on('connect', () => {
// //   // На всякий случай подтянем свежий токен (если успел смениться)
// //   refreshAuthFromLocalStorage();

// //   // Пере-войти за стол, если есть сохранённый
// //   rejoinIfNeeded();

// //   window.dispatchEvent(new CustomEvent('long-conn', {
// //     detail: { state: 'online' },
// //   }));
// // });

// // // потеря соединения
// // longSocket.on('disconnect', (reason) => {
// //   window.dispatchEvent(new CustomEvent('long-conn', {
// //     detail: { state: 'offline', reason: toErrText(reason) },
// //   }));
// // });

// // // ошибки при коннекте
// // longSocket.on('connect_error', (err) => {
// //   window.dispatchEvent(new CustomEvent('long-conn', {
// //     detail: { state: 'error', message: toErrText(err) },
// //   }));
// // });

// // // попытка реконнекта (через менеджер сокета)
// // longSocket.io.on('reconnect_attempt', (attempt) => {
// //   // перед каждой попыткой — освежаем токен
// //   refreshAuthFromLocalStorage();

// //   window.dispatchEvent(new CustomEvent('long-conn', {
// //     detail: { state: 'reconnecting', attempt },
// //   }));
// // });

// // // успешно реконнектнулись
// // longSocket.io.on('reconnect', (attempt) => {
// //   window.dispatchEvent(new CustomEvent('long-conn', {
// //     detail: { state: 'online', recovered: true, attempt },
// //   }));
// //   // далее сработает обычный 'connect', где вызовется rejoinIfNeeded()
// // });

// // export default longSocket;


// // import { io } from 'socket.io-client';

// // const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

// // const longSocket = io(`${SOCKET_URL}/long`, {
// //   autoConnect: false,
// //   transports: ['websocket'], // при отладке можно временно убрать
// //   withCredentials: false,
// // });

// // /* ⬇️ ДОБАВЬ */
// // export function ensureLongSocketAuthAndConnect() {
// //   try {
// //     const token = localStorage.getItem('token');
// //     if (token) longSocket.auth = { token };
// //   } catch {}
// //   if (!longSocket.connected && !longSocket.connecting) longSocket.connect();
// // }
// // /* ⬆️ ДОБАВЛЕНО */

// // export default longSocket;






// // // longSocket.js
// // import { io } from 'socket.io-client';

// // const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// // const longSocket = io(`${API_URL}/long`, {
// //   autoConnect: false,
// //   transports: ['websocket'],   // ← ОБЯЗАТЕЛЬНО
// //   withCredentials: false,
// // });

// // export default longSocket;
